import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Upload, Loader2, AlertTriangle, CheckCircle2, Database, Wand2, Layers, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// Keep only remembered mappings whose source is still unmatched.
function prefillRemembered(probe) {
  const unmatched = new Set(((probe?.field_analysis?.unmatched) || []).map((u) => u.source));
  const out = {};
  Object.entries(probe?.remembered || {}).forEach(([k, v]) => { if (unmatched.has(k) && v) out[k] = v; });
  return out;
}

export function ImportReportStructureDialog({ models = [], onImported }) {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);
  const [dlg, setDlg] = useState(null); // {mode:'single'|'pack', ...}

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    let data;
    try { data = JSON.parse(await file.text()); }
    catch { toast.error('That file is not valid JSON'); return; }
    setBusy(true);
    try {
      if (data?._inzara_report_pack && Array.isArray(data.reports)) {
        const probe = await api.importReportPack(data, { probe: true });
        setDlg({ mode: 'pack', pack: data, probe, createModels: (data.models || []).length > 0 });
        setOpen(true);
        return;
      }
      if (data?._inzara_report_export && data?.report) {
        const probe = await api.importReport(data, { probe: true });
        const embedded = data.embedded_model;
        const conflict = probe.embedded_model_conflict;
        setDlg({
          mode: 'single',
          bundle: data,
          probe,
          name: probe.report_name || data.report?.name || 'Imported report',
          // If a same-named model exists, default to REUSING it (bind, then remap);
          // if there's an embedded model and nothing matches, default to creating it.
          modelId: conflict ? conflict.existing_model_id : (probe.resolved_model_id || (models[0]?.model_id || '')),
          fieldMap: prefillRemembered(probe),
          createModel: !!embedded && probe.needs_model && !conflict,
          conflict,
          diff: null,
          showDiff: false,
        });
        setOpen(true);
        return;
      }
      toast.error('Not an INZARA report export or pack file');
    } catch (err) { toast.error(err.message || 'Could not read the file'); }
    finally { setBusy(false); }
  };

  const reProbe = async (modelId) => {
    if (!dlg?.bundle || !modelId) return;
    setProbing(true);
    try {
      const probe = await api.importReport(dlg.bundle, { model_id: modelId, probe: true });
      setDlg((d) => ({ ...d, probe, fieldMap: prefillRemembered(probe) }));
    } catch (err) { toast.error(err.message || 'Could not analyse fields'); }
    finally { setProbing(false); }
  };

  const onModelChange = (v) => { setDlg((d) => ({ ...d, modelId: v })); reProbe(v); };

  const loadDiff = async () => {
    if (!dlg?.bundle?.embedded_model || !dlg?.conflict) return;
    setDlg((d) => ({ ...d, showDiff: true }));
    if (dlg.diff) return;
    try {
      const diff = await api.reportImportModelDiff(dlg.bundle.embedded_model, dlg.conflict.existing_model_id);
      setDlg((d) => ({ ...d, diff }));
    } catch (err) { toast.error(err.message || 'Could not compute diff'); }
  };

  // Reuse-vs-create choice for a self-contained import that hits a same-named model.
  const chooseReuse = () => setDlg((d) => ({ ...d, createModel: false, modelId: d.conflict.existing_model_id }));
  const chooseCreate = () => setDlg((d) => ({ ...d, createModel: true }));
  React.useEffect(() => {
    if (dlg?.mode === 'single' && dlg.conflict && !dlg.createModel && dlg.modelId === dlg.conflict.existing_model_id && !dlg.probe?.field_analysis) {
      reProbe(dlg.conflict.existing_model_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dlg?.createModel]);

  const createModelsFromPack = async (pack) => {
    const overrides = {}; const created = [];
    for (const emb of (pack.models || [])) {
      const oldId = emb?.model?.model_id;
      const res = await api.importSemanticModel(emb, { mode: 'new' });
      if (res?.model_id) {
        if (oldId) overrides[oldId] = res.model_id;
        created.push({ model_id: res.model_id, name: res.name || emb?.model?.name });
      }
    }
    return { overrides, created };
  };

  const doImport = async () => {
    if (!dlg) return;
    setBusy(true);
    try {
      if (dlg.mode === 'pack') {
        let overrides = {}; let created = [];
        if (dlg.createModels && (dlg.pack.models || []).length) {
          ({ overrides, created } = await createModelsFromPack(dlg.pack));
          if (created.length) toast.success(`Imported ${created.length} model${created.length > 1 ? 's' : ''}`);
        }
        const res = await api.importReportPack(dlg.pack, {
          create_models: dlg.createModels, model_id_overrides: overrides, models_created: created,
        });
        setOpen(false); setDlg(null);
        onImported?.(res);
        return;
      }
      // single
      const embedded = dlg.bundle?.embedded_model;
      let targetModelId = dlg.modelId;
      if (dlg.createModel && embedded) {
        const mres = await api.importSemanticModel(embedded, { mode: 'new' });
        targetModelId = mres.model_id;
        toast.success(`Imported model "${mres.name || 'model'}"`);
      }
      if (!targetModelId) { toast.error('Choose a semantic model to map this report to'); setBusy(false); return; }
      const res = await api.importReport(dlg.bundle, {
        model_id: targetModelId, name: (dlg.name || '').trim() || undefined, field_map: dlg.fieldMap,
      });
      toast.success(`Imported "${res.name}"`);
      setOpen(false); setDlg(null);
      onImported?.(res);
    } catch (err) { toast.error(err.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  // ---- derived (single) ----
  const fa = dlg?.probe?.field_analysis;
  const needsModel = dlg?.probe?.needs_model;
  const embedded = dlg?.bundle?.embedded_model;
  const embeddedName = embedded?.model?.name || 'the bundled model';
  const conflict = dlg?.conflict;
  const autoCount = fa ? Object.keys(fa.auto_mapped || {}).length : 0;
  const unmatched = (fa?.unmatched || []);
  const targetFields = (fa?.target_fields || []);
  const rememberedCount = dlg?.fieldMap ? Object.keys(dlg.fieldMap).length : 0;
  const showMapping = dlg?.mode === 'single' && !dlg.createModel;
  const canImport = !busy && (dlg?.mode === 'pack'
    ? true
    : (dlg?.createModel ? !!embedded : !!dlg?.modelId));

  return (
    <>
      <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={onFile} data-testid="import-report-file" />
      <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy && !open}
        className="rounded-xl h-8 text-xs gap-1.5" data-testid="import-report-btn">
        {busy && !open ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) setDlg(null); } }}>
        <DialogContent className="max-w-lg" data-testid="import-report-dialog">
          <DialogHeader>
            <DialogTitle>{dlg?.mode === 'pack' ? 'Import report pack' : 'Import report'}</DialogTitle>
            <DialogDescription>
              {dlg?.mode === 'pack'
                ? 'Bring in several reports at once, optionally creating their bundled models.'
                : 'Bring in a report structure and bind it to a semantic model on this server.'}
            </DialogDescription>
          </DialogHeader>

          {/* ---------- PACK ---------- */}
          {dlg?.mode === 'pack' && (
            <div className="space-y-4" data-testid="import-pack-body">
              <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 p-3">
                <Layers className="w-4 h-4 text-primary shrink-0" />
                <p className="text-xs text-foreground">
                  <span className="font-semibold">{dlg.probe?.report_count || 0}</span> report{(dlg.probe?.report_count || 0) !== 1 ? 's' : ''}
                  {(dlg.probe?.model_count || 0) > 0 && <> · <span className="font-semibold">{dlg.probe.model_count}</span> bundled model{dlg.probe.model_count !== 1 ? 's' : ''}</>}
                </p>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border border-border/40 divide-y divide-border/30" data-testid="import-pack-list">
                {(dlg.probe?.report_names || []).map((nm, i) => (
                  <div key={i} className="px-3 py-1.5 text-xs text-foreground truncate">{nm}</div>
                ))}
              </div>
              {(dlg.pack.models || []).length > 0 && (
                <label className="flex items-start gap-2.5 rounded-md border border-border bg-muted/30 p-2.5 cursor-pointer" data-testid="import-pack-embedded-row">
                  <Checkbox checked={dlg.createModels} onCheckedChange={(c) => setDlg((d) => ({ ...d, createModels: !!c }))} className="mt-0.5" data-testid="import-pack-create-models" />
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-foreground"><Database className="w-3.5 h-3.5 text-primary" /> Also import the {dlg.pack.models.length} bundled model{dlg.pack.models.length > 1 ? 's' : ''}</div>
                    <p className="text-[11px] leading-snug text-muted-foreground">Creates the models on this server and binds each report to its own. Use this on a brand-new server.</p>
                  </div>
                </label>
              )}
              <p className="text-[11px] text-muted-foreground">Reports whose model isn't found (and isn't being created) are skipped.</p>
            </div>
          )}

          {/* ---------- SINGLE ---------- */}
          {dlg?.mode === 'single' && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label className="text-xs">Report name</Label>
                <Input value={dlg.name} onChange={(e) => setDlg((d) => ({ ...d, name: e.target.value }))} data-testid="import-report-name" />
              </div>

              {/* Embedded model handling */}
              {embedded && conflict && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-2" data-testid="import-report-model-conflict">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-[11px] leading-snug text-muted-foreground">A model named <span className="font-medium text-foreground">"{conflict.existing_name}"</span> already exists on this server.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant={!dlg.createModel ? 'default' : 'outline'} className="h-7 text-xs" onClick={chooseReuse} data-testid="conflict-reuse">Reuse existing</Button>
                    <Button size="sm" variant={dlg.createModel ? 'default' : 'outline'} className="h-7 text-xs" onClick={chooseCreate} data-testid="conflict-create">Create a copy</Button>
                    <button className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 ml-auto" onClick={loadDiff} data-testid="conflict-view-diff"><GitCompare className="w-3 h-3" /> View differences</button>
                  </div>
                  {dlg.showDiff && (
                    <div className="rounded border border-border/40 bg-background/60 p-2 text-[11px]" data-testid="model-diff-panel">
                      {!dlg.diff ? <div className="flex items-center gap-1 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Comparing…</div> : (
                        <div className="space-y-1">
                          <div className="text-muted-foreground">Existing: {dlg.diff.existing?.entities}e · {dlg.diff.existing?.fields}f · {dlg.diff.existing?.measures}m &nbsp;→&nbsp; Incoming: {dlg.diff.incoming?.entities}e · {dlg.diff.incoming?.fields}f · {dlg.diff.incoming?.measures}m</div>
                          {['entities', 'fields', 'measures'].map((k) => {
                            const add = dlg.diff.added?.[k] || []; const rem = dlg.diff.removed?.[k] || [];
                            if (!add.length && !rem.length) return null;
                            return (
                              <div key={k}>
                                <span className="uppercase text-[9px] text-muted-foreground tracking-wide">{k}</span>
                                {add.length > 0 && <div className="text-emerald-500">+ {add.slice(0, 8).join(', ')}{add.length > 8 ? ` +${add.length - 8}` : ''}</div>}
                                {rem.length > 0 && <div className="text-red-400">− {rem.slice(0, 8).join(', ')}{rem.length > 8 ? ` +${rem.length - 8}` : ''}</div>}
                              </div>
                            );
                          })}
                          {['entities', 'fields', 'measures'].every((k) => !(dlg.diff.added?.[k] || []).length && !(dlg.diff.removed?.[k] || []).length) && <div className="text-emerald-500">Identical structure — safe to reuse.</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {embedded && !conflict && (
                <label className="flex items-start gap-2.5 rounded-md border border-border bg-muted/30 p-2.5 cursor-pointer" data-testid="import-report-embedded-row">
                  <Checkbox checked={dlg.createModel} onCheckedChange={(c) => setDlg((d) => ({ ...d, createModel: !!c }))} className="mt-0.5" data-testid="import-report-create-model" />
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-foreground"><Database className="w-3.5 h-3.5 text-primary" /> Also import the bundled semantic model</div>
                    <p className="text-[11px] leading-snug text-muted-foreground">Creates a new model <span className="font-medium text-foreground">"{embeddedName}"</span> and binds the report to it.</p>
                  </div>
                </label>
              )}

              {dlg.createModel ? (
                <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 p-2.5" data-testid="import-report-willcreate">
                  <Wand2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-snug text-muted-foreground">A new model will be created from the bundle and the report's fields will bind automatically.</p>
                </div>
              ) : (
                <>
                  {needsModel && !conflict ? (
                    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5" data-testid="import-report-needs-model">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] leading-snug text-muted-foreground">
                        This report was built on <span className="font-medium text-foreground">{dlg.probe?.original?.model_name || dlg.probe?.original?.model_id || 'an unknown model'}</span>, which isn't on this server. Pick a semantic model to map it to.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2.5" data-testid="import-report-matched-model">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <p className="text-[11px] leading-snug text-muted-foreground">
                        Binding to model <span className="font-medium text-foreground">{dlg.probe?.resolved_model_name || dlg.probe?.resolved_model_id}</span>. You can change it below.
                      </p>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs">Semantic model</Label>
                    <Select value={dlg.modelId} onValueChange={onModelChange}>
                      <SelectTrigger data-testid="import-report-model-select"><SelectValue placeholder="Choose a model" /></SelectTrigger>
                      <SelectContent>
                        {models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {models.length === 0 && <p className="text-[11px] text-amber-400">No semantic models on this server yet — import a model first.</p>}
                  </div>

                  {showMapping && dlg.modelId && (
                    <div className="space-y-2" data-testid="import-report-field-mapping">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Field mapping</Label>
                        {probing && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                      </div>
                      {autoCount > 0 && (
                        <p className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="import-report-automatched">
                          <CheckCircle2 className="w-3 h-3" /> {autoCount} field{autoCount > 1 ? 's' : ''} matched automatically
                        </p>
                      )}
                      {rememberedCount > 0 && (
                        <p className="text-[11px] text-primary flex items-center gap-1" data-testid="import-report-remembered">
                          <Wand2 className="w-3 h-3" /> Pre-filled {rememberedCount} mapping{rememberedCount > 1 ? 's' : ''} from a previous import
                        </p>
                      )}
                      {unmatched.length === 0 ? (
                        !probing && <p className="text-[11px] text-muted-foreground">All fields bind to this model — nothing to map.</p>
                      ) : (
                        <>
                          <p className="text-[11px] text-muted-foreground">{unmatched.length} field{unmatched.length > 1 ? "s don't" : " doesn't"} match this model. Map them so the visuals bind (or leave unmapped to skip).</p>
                          <div className="space-y-2">
                            {unmatched.map((u) => {
                              const opts = targetFields.filter((t) => !u.kind || t.kind === u.kind);
                              const options = opts.length ? opts : targetFields;
                              return (
                                <div key={u.source} className="flex items-center gap-2" data-testid={`remap-row-${u.source}`}>
                                  <div className="w-40 shrink-0 flex items-center gap-1.5 min-w-0">
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">{u.kind === 'measure' ? 'M' : 'D'}</Badge>
                                    <span className="text-xs truncate" title={u.bare}>{u.bare}</span>
                                  </div>
                                  <span className="text-muted-foreground text-xs">→</span>
                                  <Select value={dlg.fieldMap[u.source] || '__none__'} onValueChange={(v) => setDlg((d) => { const fm = { ...d.fieldMap }; if (v === '__none__') delete fm[u.source]; else fm[u.source] = v; return { ...d, fieldMap: fm }; })}>
                                    <SelectTrigger className="h-8 text-xs" data-testid={`remap-select-${u.source}`}><SelectValue placeholder="Leave unmapped" /></SelectTrigger>
                                    <SelectContent className="max-h-72">
                                      <SelectItem value="__none__">Leave unmapped</SelectItem>
                                      {options.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}{t.entity && t.entity !== 'Measures' ? ` · ${t.entity}` : ''}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setOpen(false); setDlg(null); }} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={doImport} disabled={!canImport} data-testid="import-report-confirm">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} {dlg?.mode === 'pack' ? 'Import all' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
